import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';
import pattern_config from '#core/pattern-config';
import expandable from '#partial/expandable/expandable';


const jump_nav_$ = '.jump-nav';
const sidebar_$ = '.sidebar';
const gate_$ = '[data-gate]';
const expandable_$ = '.expandable--jump-nav';
const fixed_c = 'jump-nav--fixed';
const shown_c = 'jump-nav--fixed-shown';
const placeholder_c = 'jump-nav__placeholder';
const height_var = '--jump-nav-natural-height';
const palette_attr = 'data-palette-dropdown';
const palette_class_prefix = ':palette-';
const outside_close_attr = 'data-expandable-outside-close';
const escape_close_attr = 'data-expandable-escape-close';
const desktop_breakpoint = pattern_config.jump_nav.layout_switch;
const slide_duration_ms = 300;
const enter_delay_ms = 200;
const enter_settle_ms = 250;

let has_jump_nav = false;


const module = {

	init() {
		if (load.ran('jump-nav')) return false;

		load.all(() => {
			has_jump_nav = setup();
			if (!has_jump_nav) return false;
		});
	},

};

export default module;
module.init();


/**
 * Set up scroll detection for each jump-nav
 * @return {boolean}
 */
function setup() {
	const $jump_navs = document.querySelectorAll(jump_nav_$);
	if (!$jump_navs.length) return false;

	$jump_navs.forEach(setup_jump_nav);

	return true;
}


/**
 * Set up scroll detection for a single jump-nav. Once the surrounding sidebar
 * has scrolled completely off the top of the viewport, the jump-nav slides
 * down into a fixed bar with a floating dropdown menu. Fixed mode is
 * desktop-only; mobile keeps its in-flow button+dropdown layout.
 * @param {element} $jump_nav
 */
function setup_jump_nav($jump_nav) {
	const $sidebar = $jump_nav.closest(sidebar_$) || $jump_nav.parentElement;
	if (!$sidebar) return;

	const $expandable = $jump_nav.querySelector(expandable_$);
	const palette_value = $jump_nav.getAttribute(palette_attr);
	const palette_class = palette_value
		? palette_class_prefix + palette_value
		: null;

	// Placeholder inside the sidebar preserves the sidebar's height when the
	// jump-nav lifts out of flow, keeping the sidebar's rect bottom a stable
	// reference for the mode-switch trigger.
	const $placeholder = document.createElement('div');
	$placeholder.className = placeholder_c;
	$placeholder.setAttribute('aria-hidden', 'true');
	$jump_nav.parentNode.insertBefore($placeholder, $jump_nav.nextSibling);

	const sync_placeholder_height = () => {
		if ($jump_nav.classList.contains(fixed_c)) return;
		$placeholder.style.setProperty(height_var, $jump_nav.offsetHeight + 'px');
	};
	sync_placeholder_height();

	let is_fixed = false;
	let mode_timeout = null;

	const update = () => {
		const is_desktop = layout.isBreakpointMin(desktop_breakpoint);
		const sidebar_rect = $sidebar.getBoundingClientRect();
		const has_gate = !!document.querySelector(gate_$);
		const next = is_desktop && sidebar_rect.bottom < 0 && !has_gate;
		if (next === is_fixed) return;

		is_fixed = next;
		if (mode_timeout) {
			clearTimeout(mode_timeout);
			mode_timeout = null;
		}

		if (next) {
			mode_timeout = setTimeout(() => {
				enter_fixed($jump_nav, $expandable, palette_class);
				mode_timeout = null;
			}, enter_delay_ms);
		}
		else {
			$jump_nav.classList.remove(shown_c);
			mode_timeout = setTimeout(() => {
				leave_fixed_complete($jump_nav, $expandable, palette_class, is_desktop);
				sync_placeholder_height();
				mode_timeout = null;
			}, slide_duration_ms);
		}
	};

	const observer = new IntersectionObserver(update, { threshold: 0 });
	observer.observe($sidebar);

	window.addEventListener(g.scroll_e, update, { passive: true });
	window.addEventListener(g.resize_e, () => {
		sync_placeholder_height();
		update();
	}, { passive: true });
	update();
}


/**
 * Enter fixed mode: close the dropdown instantly (no flash during slide),
 * enable click-outside and escape dismissal, apply the dropdown palette, and
 * slide the bar down into view.
 * @param {element} $jump_nav
 * @param {element} $expandable
 * @param {string|null} palette_class
 */
function enter_fixed($jump_nav, $expandable, palette_class) {
	force_toggle_instantly($expandable, false);
	set_dismiss_behavior($expandable, true);
	if (palette_class) $jump_nav.classList.add(palette_class);

	// Snap to the off-screen start state with no transition, then restore
	// the transition before triggering the slide-down. Otherwise the browser
	// animates transform from none(0) → -100% and gets interrupted by shown_c,
	// so the bar never reaches the off-screen start and looks like it pops in.
	$jump_nav.style.transition = 'none';
	$jump_nav.classList.add(fixed_c);
	void $jump_nav.offsetHeight;
	$jump_nav.style.transition = '';

	setTimeout(() => {
		$jump_nav.classList.add(shown_c);
	}, enter_settle_ms);
}


/**
 * Finalize leaving fixed mode after the slide-out animation: clear fixed-mode
 * classes and restore the sidebar's always-open expandable on desktop.
 * @param {element} $jump_nav
 * @param {element} $expandable
 * @param {string|null} palette_class
 * @param {boolean} is_desktop
 */
function leave_fixed_complete($jump_nav, $expandable, palette_class, is_desktop) {
	$jump_nav.classList.remove(fixed_c);
	if (palette_class) $jump_nav.classList.remove(palette_class);
	set_dismiss_behavior($expandable, false);
	if (is_desktop) {
		force_toggle_instantly($expandable, true);
	}
}


/**
 * Toggle an expandable to a given state with no transition, so layout changes
 * around it can happen in a single frame.
 * @param {element} $expandable
 * @param {boolean} force
 */
function force_toggle_instantly($expandable, force) {
	if (!$expandable) return;
	const previous = $expandable.style.transition;
	$expandable.style.transition = 'none';
	expandable.toggle({ id: $expandable.id, force: force });
	void $expandable.offsetHeight;
	$expandable.style.transition = previous;
}


/**
 * Toggle click-outside and Escape dismissal for the expandable. Disabled on
 * the sidebar-mode render so outside clicks don't collapse the always-open
 * desktop sidebar.
 * @param {element} $expandable
 * @param {boolean} enabled
 */
function set_dismiss_behavior($expandable, enabled) {
	if (!$expandable) return;
	const value = enabled ? g.true_s : g.false_s;
	$expandable.setAttribute(outside_close_attr, value);
	$expandable.setAttribute(escape_close_attr, value);
}
